// El panel lateral del modo Modelo: donde se elige TODO lo que la columna de
// configuración no resuelve en su lugar —productos, modelo, pose, fondo y formato—. Un
// solo contenedor para todos: se abren en el mismo lugar, con la misma forma, y lo único
// que cambia es la grilla de adentro y el título
// (ADR-2026-08-21-modelo-v2-un-solo-panel-lateral).
//
// Antes la grilla de productos ERA una de las dos columnas de la pantalla y ocupaba su
// mitad todo el tiempo, incluso después de elegir. Acá es un panel que se abre sobre el
// área de la derecha cuando hace falta —lo dispara la zona de subida de
// `PanelCompacto`— y se va cuando terminás: el modo se ve entero desde el primer
// momento y la grilla aparece a tamaño cómodo, con todos los productos cargados a la
// vista, en vez de a tres columnas dentro de una franja angosta.
//
// Ver ADR-2026-08-21-modelo-v2-una-sola-columna. El Catálogo tiene su gemelo en
// `components/PanelLateralModo` (ADR-2026-08-24-catalogo-toma-el-diseno-de-modelo).

import React from 'react';
import { X } from 'lucide-react';

export interface ProductosLateralProps {
  abierto: boolean;
  onCerrar: () => void;
  /** Qué se está eligiendo — encabeza el panel. */
  titulo: string;
  /**
   * Línea de ayuda debajo del título: la regla de esta selección
   * ("Podés elegir hasta 3 modelos" / "Llegaste al máximo…").
   *
   * Va en la cabecera del panel y no arriba de la grilla porque es la regla del
   * panel entero, no de una fila; y ahí no se va con el scroll, que es cuando
   * hace falta releerla (ADR-2026-08-25-tres-modelos-como-maximo).
   */
  subtitulo?: React.ReactNode;
  /**
   * Ancho máximo del panel, en px.
   *
   * Lo pone quien lo abre y no el panel: cada grilla necesita lo suyo. La de productos
   * es larga y va a tarjetas chicas, así que aprovecha el ancho; las de modelo y pose
   * son de tres columnas y con el mismo ancho quedaban tarjetas enormes con mucho aire
   * entre medio.
   */
  maxAncho?: number;
  /**
   * Dónde empieza el panel, medido desde el borde izquierdo del área de las dos
   * columnas.
   *
   * Acepta número de px o valor CSS: quien lo abre MIDE el borde derecho de la columna
   * de configuración con un `ResizeObserver` y pasa el número —el ancho de esa columna
   * es elástico (`clamp(420px, 34.375vw, 660px)`) y cambia también con el paso, así que
   * escribirlo acá era condenar al panel a taparla en cuanto cualquiera de las dos cosas
   * cambiara—. El valor CSS queda para el primer render, antes de que la medición
   * exista. Ver ADR-2026-09-02-el-panel-lateral-arranca-donde-termina-la-configuracion.
   */
  offsetIzquierdo: string | number;
  /**
   * El área de la pantalla del modo (las dos columnas).
   *
   * Un click ahí adentro y fuera del panel lo cierra — incluido el que cae en la columna
   * de configuración, que el velo de este componente no cubre. Se acota a ese contenedor
   * a propósito: los sheets que se abren DESDE el panel (subir producto, "Ver más" de
   * poses) se montan en portales colgados del `body`, fuera de él, así que operarlos no
   * cierra lo que quedó abierto detrás.
   */
  contenedorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

export const ProductosLateral: React.FC<ProductosLateralProps> = ({
  abierto,
  onCerrar,
  titulo,
  subtitulo,
  maxAncho = 820,
  offsetIzquierdo,
  contenedorRef,
  children,
}) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  // Escape cierra, como cualquier capa que se monta encima. El listener sólo vive
  // mientras el panel está abierto: registrado siempre, se comería el Escape de los
  // sheets que se abren DESDE acá (subir producto, por ejemplo).
  React.useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto, onCerrar]);

  // Click afuera, en cualquier lado de la pantalla del modo. En `mousedown` y no en
  // `click`: los disparadores de la columna (la zona de subida, las cards de Modelo y
  // Pose) abren en `click`, así que el orden queda cerrar-y-volver-a-abrir, y tocar el
  // disparador de OTRO picker cambia de panel en un solo gesto.
  React.useEffect(() => {
    if (!abierto) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (!contenedorRef.current?.contains(target)) return;
      // Los DISPARADORES no cuentan como "afuera": abren en `click`, así que sin esto el
      // `mousedown` cerraba el panel y el `click` lo volvía a abrir. El estado final era
      // el correcto, pero el panel se desmontaba y montaba en cada tap —parpadeaba,
      // reiniciaba su animación de entrada y perdía el scroll de la grilla—. Tocar la
      // card de otro picker igual cambia de panel: eso lo hace su `onClick`.
      if (
        target instanceof Element &&
        target.closest('[data-abre-panel-lateral]')
      ) {
        return;
      }
      onCerrar();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [abierto, onCerrar, contenedorRef]);

  if (!abierto) return null;

  return (
    // `absolute` y no `fixed`: el panel se posiciona dentro del área de la pantalla del
    // modo, respetando el ancho de la columna de configuración que queda a su izquierda
    // —esa columna sigue siendo usable con el panel abierto, que es la gracia de que
    // esto no sea un modal—.
    <div
      // `justify-start` + `items-start`: el panel se PEGA al borde de la columna de
      // configuración y arranca a su misma altura, en vez de centrarse en el área libre.
      // Centrado quedaba flotando —desalineado del techo de la columna en horizontal, y
      // en el medio de la nada en vertical—, sin relación visible con el control que lo
      // abrió.
      className="absolute inset-y-0 right-0 z-30 flex items-start justify-start pointer-events-none"
      style={{ left: offsetIzquierdo }}
    >
      {/* Click afuera cierra. Sin velo oscuro: no es un modal —lo de atrás sigue
          respondiendo— y un velo diría lo contrario. */}
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={onCerrar}
        aria-hidden="true"
      />
      {/* No ocupa el área entera: la grilla no necesita 1200px de ancho ni el alto
          completo de la ventana para mostrar productos, y a tamaño completo el panel se
          leía como una pantalla nueva en vez de como una capa sobre la que ya está.
          Topes en px para el ancho —lo que tiene que entrar son N tarjetas, que miden
          igual en cualquier monitor— y el alto es FIJO: el área disponible menos los
          24px que le dejan aire abajo. Fijo y no un tope: con `max-h` cada picker medía
          lo que midiera su contenido —fondos, que es el más corto, abría a 508px contra
          los 816 de modelo y pose—, y cambiar de picker movía el panel de tamaño como si
          fuera otra cosa. Arranca pegado a la columna de configuración, alineado con su
          borde de arriba. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-labelledby="panel-lateral-titulo"
        className="pointer-events-auto relative mr-6 w-full h-[calc(100%-1.5rem)] flex flex-col rounded-3xl bg-white border border-black/[0.06] shadow-[0_12px_48px_rgba(0,0,0,0.14)] overflow-hidden animate-paso-titulo"
        style={{ maxWidth: maxAncho }}
      >
        {/* Cabecera propia: el panel dice qué es y cómo se cierra en la misma fila. La X
            estaba flotando sobre el buscador, sin nada que la anclara. */}
        <div className="shrink-0 flex items-start justify-between gap-3 px-6 pt-5 pb-1">
          <div className="min-w-0">
            <h2
              id="panel-lateral-titulo"
              className="text-[17px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90"
            >
              {titulo}
            </h2>
            {subtitulo && (
              <p className="mt-0.5 text-[12.5px] leading-snug text-[#3C3C43]/60">
                {subtitulo}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="shrink-0 w-8 h-8 grid place-items-center rounded-full text-brand-black/45 hover:bg-black/[0.05] hover:text-brand-black/80 transition-colors"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};
